import React, { FC, useState } from "react";
import styled from "styled-components";
import { font } from "../lib/sharedStyles";
import { FormattedCategory } from "./CategoryWrapper";

interface CategoryModelCardProps extends FormattedCategory {
  slideBackground: string;
  slideHoverBackground: string;
  transparentProductImage: boolean;
}

const Card = styled.a<{
  $isHovered: boolean;
  $background: string;
  $hoverBackground: string;
}>`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  width: 220px;
  height: 100%;
  min-height: 412px;
  text-decoration: none;
  overflow: hidden;
  transition: background-color 0.3s ease-in-out;
  background-color: ${({ $isHovered, $background, $hoverBackground }) =>
    $isHovered ? $hoverBackground : $background};

  @media (min-width: 1024px) {
    width: 421px;
    min-height: 715px;
  }
`;

/**
 * This layer sits behind the text/CTA and holds
 * the background image. We scale this layer on hover.
 */
const BackgroundImage = styled.div<{
  $isHovered: boolean;
  $imageUrl?: string;
  $hoverImageUrl?: string;
  $transparent?: boolean;
}>`
  position: absolute;
  inset: 0;
  background-image: url(${({ $isHovered, $imageUrl, $hoverImageUrl }) =>
    $isHovered && $hoverImageUrl ? $hoverImageUrl : $imageUrl});
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
  transition: transform 0.6s ease-in-out, opacity 0.3s ease-in-out;
  transform-origin: center top;
  transform: scale(${({ $isHovered }) => ($isHovered ? 1.05 : 1)});
  opacity: ${({ $transparent }) => ($transparent ? 0.8 : 1)};
`;

const ContentContainer = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

/* Acts as a spacer to push the content towards the bottom */
const Spacer = styled.div`
  min-height: 324px;
`;

/* Contains text and CTA */
const ContentInner = styled.div`
  padding: 0 16px 26px;

  @media (min-width: 1024px) {
    padding: 0 24px 24px;
  }
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Title = styled.span`
  ${font("Instrument Sans", 600, "10px", "16px")}
  letter-spacing: 0.1px;

  @media (min-width: 1024px) {
    ${font("Instrument Sans", 600, "14px", "20px")}
    letter-spacing: 0.14px;
  }
`;

const Description = styled.span`
  ${font("Futura", 500, "16px", "16px")}
  letter-spacing: -0.32px;
  margin-top: 8px;

  @media (min-width: 1024px) {
    ${font("Futura", 500, "24px", "24px")}
    letter-spacing: -0.48px;
    margin-top: 16px;
  }
`;

const CTAWrapper = styled.div<{ $isHovered: boolean }>`
  display: flex;
  justify-content: center;
  align-items: flex-end;
  margin-top: 8px;

  svg {
    fill: #fff;
    background: #000;
    border-radius: 50%;
  }

  @media (min-width: 1024px) {
    margin-top: 16px;
  }
`;

const CategoryModelCard: FC<CategoryModelCardProps> = ({
  url,
  title,
  description,
  imageUrl,
  hoverImageUrl,
  slideBackground,
  slideHoverBackground,
  transparentProductImage,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Card
      href={url}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      $isHovered={isHovered}
      $background={slideBackground}
      $hoverBackground={slideHoverBackground}
    >
      <BackgroundImage
        $isHovered={isHovered}
        $imageUrl={imageUrl}
        $hoverImageUrl={hoverImageUrl}
        $transparent={transparentProductImage}
      />

      <ContentContainer>
        <Spacer />
        <ContentInner>
          <TextContainer>
            {title && <Title>{title}</Title>}
            {description && <Description>{description}</Description>}
          </TextContainer>
          {url && (
            <CTAWrapper $isHovered={isHovered}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="32"
                height="32"
                viewBox="0 0 32 32"
                fill="none"
                aria-label="Call to action"
              >
                <path
                  d="M11 15.25C10.5858 15.25 10.25 15.5858 10.25 16C10.25 16.4142 10.5858 16.75 11 16.75V15.25ZM21.5303 16.5303C21.8232 16.2374 21.8232 15.7626 21.5303 15.4697L16.7574 10.6967C16.4645 10.4038 15.9896 10.4038 15.6967 10.6967C15.4038 10.9896 15.4038 11.4645 15.6967 11.7574L19.9393 16L15.6967 20.2426C15.4038 20.5355 15.4038 21.0104 15.6967 21.3033C15.9896 21.5962 16.4645 21.5962 16.7574 21.3033L21.5303 16.5303ZM11 16.75H21V15.25H11V16.75Z"
                  fill="#fff"
                />
              </svg>
            </CTAWrapper>
          )}
        </ContentInner>
      </ContentContainer>
    </Card>
  );
};

export default CategoryModelCard;
